import { type FC, useEffect } from 'react'

import { useUnit } from 'effector-react'

import { IconLoading } from '@/assets/icons/icon-loading.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import {
  $timelineData,
  getTimelineDataFx,
} from '@/dialogs/PPVTimelineDialog/models.ts'
import type { DialogProps } from '@/dialogs/types'
import { $sentMedia } from '@/models/chattingUI/media.ts'
import { loadMultipleMediasFx } from '@/models/vault/medias'
import {
  startClonedMediaByOriginalsPolling,
  stopClonedMediaByOriginalsPolling,
} from '@/models/vault/medias/clonned.ts'
import { classNames } from '@/utils/helpers.ts'

import NoDataIcon from '../../modules/chatting/icons/no-data.svg'
import { TimelineChart } from './chart.tsx'

interface Props extends DialogProps {}

export const PPVTimelineDialog: FC<Props> = ({ onClose, isOpen }) => {
  const sentMedia = useUnit($sentMedia)
  const isLoadingMedia = useUnit(loadMultipleMediasFx.pending)
  const timelineData = useUnit($timelineData)
  const isChartVisible = timelineData.length > 0

  useEffect(() => {
    if (isOpen) {
      getTimelineDataFx(sentMedia)
    }
  }, [isOpen, sentMedia])

  useEffect(() => {
    if (isOpen) {
      startClonedMediaByOriginalsPolling()
    } else {
      stopClonedMediaByOriginalsPolling()
    }
  }, [isOpen])

  return (
    <Dialog
      show={isOpen}
      bigDialog
      showCloseIcon={!isChartVisible}
      onClose={onClose}
      childrenClassName="max-w-full overflow-hidden"
      panelClassName={classNames('wide-scrollbar', {
        'lg:max-w-[980px] 2xl:max-w-[1312px]': isChartVisible,
        'lg:max-w-[648px] 2xl:max-w-[648px]': timelineData?.length === 0,
      })}
    >
      {timelineData.length ? (
        <>
          <Dialog.Header>
            <Dialog.Title>PPV Timeline</Dialog.Title>
            {isLoadingMedia && (
              <div className="text-sm flex gap-2 items-center">
                Loading media...
                <IconLoading className="h-4 w-4 animate-spin" />
              </div>
            )}
          </Dialog.Header>
          <Dialog.Description className="mt-0 block">
            <div className="mb-6">
              <TimelineChart
                data={timelineData}
                onTimelineClose={onClose}
              />
            </div>
            <Button
              className="w-full"
              onClick={onClose}
            >
              Close
            </Button>
          </Dialog.Description>
        </>
      ) : (
        <Dialog.Description className="flex flex-col items-center gap-6">
          <NoDataIcon />
          <p className="text-title text-center text-3xl font-semibold">
            You have not sent any PPVs to this fan.
          </p>
          <Button
            className="w-full"
            onClick={onClose}
          >
            OK
          </Button>
        </Dialog.Description>
      )}
    </Dialog>
  )
}
